// Authorization for Support is driven by the role_permissions matrix at the
// route boundary (ProtectedRoute section="support_tickets"). These role lists
// only shape in-module UX (lead vs technician views). super_admin/manager are
// included so admins are never bounced out of a module the matrix grants them.
import { canViewSection } from './permissionHelper';
export const SUPPORT_ROLES = ['super_admin', 'admin', 'manager', 'support_lead', 'support_tech'];

/** Sales / delivery sections a support_tech may open outside /support when granted.
 *  technicians_bucket_list is intentionally omitted — that is the admin "all techs"
 *  view; field techs use technician_bucket → My Deliveries. */
export const SUPPORT_TECH_DELIVERY_SECTIONS = [
  'technician_bucket',
  'delivery_my_deliveries',
  'delivery_challans',
  'delivery_register_management',
  'return_dc',
  'sales_quotations',
  'sales_orders_doc',
];

export const isSupportUser = (user) => user && SUPPORT_ROLES.includes(user.role);

/** Matrix-first: any role with support_tickets view may open the Support module. */
export function canAccessSupportModule(user, effectivePermissions) {
  return canViewSection(user, effectivePermissions, 'support_tickets');
}

export const isSupportTechnician = (user) => user?.role === 'support_tech';

export const SUPPORT_TICKET_ASSIGNEE_PERMISSION = 'support_ticket_assignee';

export const isSupportLead = (user) =>
  ['super_admin', 'admin', 'manager', 'support_lead'].includes(user?.role);

export const isSupportTicketAssignee = (user) =>
  Array.isArray(user?.permissions) && user.permissions.includes(SUPPORT_TICKET_ASSIGNEE_PERMISSION);

/** Warehouse lead (not a field technician) — assigned-tickets view without tech nav. */
export const isWarehouseTicketLead = (user) =>
  Boolean(user && isSupportTicketAssignee(user) && !isSupportTechnician(user) && !isSupportLead(user));

/** Warehouse lead: same ticket tools as support lead, but only tickets assigned to them. */
export const ticketAssignedToUser = (ticket, items, user) => {
  if (!user?.user_id) return false;
  const list = items || ticket?.items || [];
  return list.some((item) => Number(item.assigned_to) === Number(user.user_id));
};

export const canActAsTicketLead = (user, ticket, items) => {
  if (isSupportLead(user)) return true;
  return isSupportTicketAssignee(user) && ticketAssignedToUser(ticket, items, user);
};

/** Only real support leads may assign / reassign tickets from the list. */
export const canAssignSupportTickets = (user) => isSupportLead(user);

/** Technician or warehouse lead — only their assigned tickets, not the full queue. */
export const isAssignedTicketsOnly = (user, isAssignedDataOnly) => {
  if (!user || isSupportLead(user)) return false;
  if (isSupportTechnician(user) || isSupportTicketAssignee(user)) return true;
  return typeof isAssignedDataOnly === 'function' && isAssignedDataOnly('support_tickets');
};

/** Support lead/admin/manager or warehouse staff may close tickets. */
export const canCloseSupportTicket = (user) =>
  isSupportLead(user) || user?.role === 'warehouse';

/** Admin / super_admin / support_lead only — cancel ERP migration tickets. */
export const canCancelSupportTicket = (user) =>
  ['super_admin', 'admin', 'support_lead'].includes(user?.role);

export const canAccessCustomerInventory = (user) => {
    if (!user) return false;
    if (['admin', 'manager', 'floor_manager', 'support_lead'].includes(user.role)) return true;
    const perms = Array.isArray(user.permissions) ? user.permissions : [];
    return perms.includes('customer_inventory_access');
};

/** True when a support_tech may leave the /support shell for this path (permission-aware).
 *  Routes that already pass a ProtectedRoute section/sections check are allowed
 *  separately in ProtectedRoute — this list covers ungated / legacy paths only. */
export function supportTechnicianMayAccessPath(pathname, canView) {
  if (!pathname) return false;
  if (pathname.startsWith('/support')) return true;
  if (pathname.startsWith('/customer-inventory') && canView('customer_inventory')) return true;
  if (pathname.startsWith('/sales-pipeline')) {
    return SUPPORT_TECH_DELIVERY_SECTIONS.some((s) => canView(s));
  }
  if (pathname.startsWith('/delivery-register-management')) {
    return canView('delivery_register_management');
  }
  // Production — if User Permissions grant any floor section, allow the module.
  if (pathname.startsWith('/floor-pipeline')) {
    return [
      'floor_pipeline',
      'floor_tickets',
      'chip_level_repair',
      'qc_management',
      'dispatch_qc',
      'pending_inventory',
      'diagnosis_failed',
    ].some((s) => canView(s));
  }
  if (pathname.startsWith('/support-parts')) {
    return canView('support_part_requests') || canView('support_part_challan');
  }
  if (pathname.startsWith('/inventory-management')) {
    return [
      'inventory_management',
      'inventory_asset_movement',
      'customer_inventory',
      'ttspl_history',
      'parts_dashboard',
      'parts_inventory',
      'parts_approval',
      'parts_history',
      'parts_discarded',
      'physical_dead_parts',
      'scrap_challans',
      'part_vendor_repair',
      'dispatch_charger_warehouse',
    ].some((s) => canView(s));
  }
  return false;
}

export const postLoginPath = () => {
  // Signed in from a warehouse QR link (/scan/<token>): back to the scan.
  try {
    const p = sessionStorage.getItem('carret.afterLogin');
    sessionStorage.removeItem('carret.afterLogin');
    if (p && /^\/scan\/[a-f0-9]+$/i.test(p)) return p;
  } catch { /* storage off */ }
  return '/';
};
